import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const inputSchema = z.object({
  // Optional so the command palette (which executes with an empty payload) can
  // run this as a toggle. When omitted, we flip the current state.
  pressed: z.boolean().optional(),
});

export class CocoTogglePlanModeAction extends BaseAction<
  z.infer<typeof inputSchema>,
  { t: string; message?: string }[]
> {
  title = 'Toggle Plan Mode';
  inputSchema = inputSchema;
  group = 'Chat';
  description =
    'Plan before building: load architecture skills and plan workflows/agents first';
  // Driven by the input-toolbar toggle, not the command palette. Kept registered
  // (so useAction can resolve it) but hidden from the palette.
  discrete = true;

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: z.infer<typeof inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const previous = model.planModeWatchable.snapshot();
    // Empty payload (e.g. from the command palette) flips the current state.
    const next = params.pressed ?? !previous;

    model.planModeWatchable.set(next);

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: next ? { type: 'enter_plan_mode' } : { type: 'exit_plan_mode' },
    });

    if (result.isErr()) {
      const isNotFound =
        result.error.t === 'application' &&
        result.error.err.errorType === 'NotFound';
      if (isNotFound) {
        // Conversation not created yet; local state is correct and the next
        // submit reconciles plan mode server-side via context.planMode.
        return ok({});
      }
      model.planModeWatchable.set(previous);
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to toggle plan mode';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    z.infer<typeof inputSchema>,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.PlanModeToggled, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      // Resulting state after the toggle (the palette may not supply `pressed`).
      pressed: model.planModeWatchable.snapshot(),
    });
  }
}
